// "I'm feeling lucky" — one unseen film picked by GLM 5.2 from your taste, shown
// with its TMDB poster and blurb. "Get another one" rolls a fresh pick that never
// repeats (prior picks are sent back as an exclusion list). Lives on the solo
// dashboard; matches the printed-ticket look of the surrounding sheet.

import { useState } from "react";
import { PosterCard } from "./brand.tsx";
import { Kicker, H3, Sub } from "./sheet.tsx";
import { getSuggestion, type Suggestion } from "../lib/api.ts";
import { lookupTitle, watchProviders, type TmdbDetail, type WatchInfo } from "../lib/tmdb.ts";

interface Copy {
  kicker: string;
  title: string;
  sub: string;
  cta: string;
  rolling: string;
}
const SOLO_COPY: Copy = {
  kicker: "Something new tonight",
  title: "Watch tonight",
  sub: "One film you haven't seen, picked for your taste by the projectionist.",
  cta: "Pull the lever",
  rolling: "Consulting the reels…",
};
const BLEND_COPY: Copy = {
  kicker: "Neither of you has seen it",
  title: "Tonight's pick",
  sub: "One film chosen for both your tastes — the perfect thing to watch together.",
  cta: "Roll tonight's pick",
  rolling: "Splicing your reels…",
};

// The spinning symbols while a pick is loading — three staggered reels, like a
// slot machine window. Loop-only, so reduced-motion suppresses it (styles.css).
const REEL = ["🎬", "🍿", "🎞️", "📽️", "⭐", "🎟️"];
function SlotReel({ duration }: { duration: string }) {
  return (
    <span className="slotwin inline-block w-[1.5em] text-center" aria-hidden>
      <span className="slotreel" style={{ animationDuration: duration }}>
        {[...REEL, ...REEL].map((e, i) => (
          <span key={i}>{e}</span>
        ))}
      </span>
    </span>
  );
}
function SlotReels() {
  return (
    <span className="inline-flex items-center">
      <SlotReel duration="0.5s" />
      <SlotReel duration="0.65s" />
      <SlotReel duration="0.8s" />
    </span>
  );
}

/**
 * "I'm feeling lucky" (solo) / "Tonight's pick" (blend): one unseen film picked
 * by GLM. Pass `blendSlug` to get a joint pick neither member has seen.
 */
export function FeelingLucky({ blendSlug }: { blendSlug?: string } = {}) {
  const copy = blendSlug ? BLEND_COPY : SOLO_COPY;
  const [pick, setPick] = useState<Suggestion | null>(null);
  const [detail, setDetail] = useState<TmdbDetail | null>(null);
  const [watch, setWatch] = useState<WatchInfo | null>(null);
  const [shown, setShown] = useState<Suggestion[]>([]);
  const [loading, setLoading] = useState(false);
  const [rollKey, setRollKey] = useState(0);
  const [error, setError] = useState<string | null>(null);

  async function roll() {
    setLoading(true);
    setRollKey((k) => k + 1); // re-triggers the lever yank
    setError(null);
    try {
      const s = await getSuggestion(shown, blendSlug);
      if (!s) {
        setError("Couldn't find a fresh pick — try again in a moment.");
        return;
      }
      setPick(s);
      setShown((prev) => [...prev, s]);
      setDetail(null);
      setWatch(null);
      // Resolve poster + blurb in the background; the card renders either way.
      lookupTitle(s.title, s.year)
        .then((d) => {
          setDetail(d);
          // Then where it streams, once we have the TMDB id.
          if (d?.tmdbId) watchProviders(d.tmdbId).then(setWatch).catch(() => setWatch(null));
        })
        .catch(() => setDetail(null));
    } catch {
      setError("The projector jammed — try again.");
    } finally {
      setLoading(false);
    }
  }

  const year = detail?.releaseYear ?? pick?.year;

  return (
    <section data-testid="section-feeling-lucky" className="px-6 py-7 sm:px-10">
      <Kicker>{copy.kicker}</Kicker>
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div className="flex items-center gap-3">
          <H3 inline>{copy.title}</H3>
          <span className="rounded-pill border border-gold-600 px-2.5 py-0.5 font-mono text-[10px] font-bold uppercase tracking-stamp text-gold-600">
            ＋ AI-generated
          </span>
        </div>
        {pick && (
          <button
            type="button"
            data-testid="action-lucky-again"
            onClick={roll}
            disabled={loading}
            className="font-mono text-[11px] font-bold uppercase tracking-stamp text-ink-400 underline underline-offset-2 hover:text-red-600 disabled:opacity-50"
          >
            {loading ? "Rolling…" : "Get another one ↻"}
          </button>
        )}
      </div>

      {!pick && (
        <>
          <Sub>{copy.sub}</Sub>
          <button
            type="button"
            data-testid="action-lucky"
            onClick={roll}
            disabled={loading}
            className="mt-4 inline-flex min-h-[42px] items-center justify-center gap-2 rounded-pill bg-ink-900 px-6 text-sm font-semibold text-paper-50 transition-colors hover:bg-ink-800 disabled:opacity-60"
          >
            {loading ? (
              <>
                <span key={rollKey} className="leveryank" aria-hidden>
                  🎰
                </span>
                <SlotReels /> {copy.rolling}
              </>
            ) : (
              <>
                <span aria-hidden>🎰</span> {copy.cta}
              </>
            )}
          </button>
        </>
      )}

      {/* Re-rolls: the reels spin above the (dimmed) previous pick */}
      {pick && loading && (
        <p className="mt-3 flex items-center gap-2 text-sm text-ink-500" data-testid="lucky-rolling">
          <span key={rollKey} className="leveryank" aria-hidden>
            🎰
          </span>
          <SlotReels /> {copy.rolling}
        </p>
      )}

      {error && !loading && (
        <p className="mt-4 text-sm text-red-600" data-testid="lucky-error">
          {error}
        </p>
      )}

      {pick && (
        <div
          key={pick.title + (pick.year ?? "")}
          data-testid="lucky-result"
          className="slotland mt-4 flex gap-5 rounded-xl border border-border-strong bg-paper-50 p-4 sm:p-5"
        >
          <div className={`w-[92px] shrink-0 sm:w-[112px] ${loading ? "opacity-40" : ""}`}>
            <PosterCard title={pick.title} year={year} poster={detail?.posterPath ?? undefined} width="100%" />
          </div>
          <div className="min-w-0 flex-1">
            <h4 className="font-display text-xl font-extrabold leading-tight tracking-tightish text-ink-900 sm:text-2xl">
              {pick.title} {year && <span className="font-normal text-ink-400">({year})</span>}
            </h4>
            <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 font-mono text-[11px] uppercase tracking-stamp text-ink-500">
              {typeof detail?.voteAverage === "number" && detail.voteAverage > 0 && (
                <span className="text-gold-600" data-testid="lucky-rating">
                  ★ {detail.voteAverage.toFixed(1)} TMDB
                </span>
              )}
              {detail?.genres?.slice(0, 3).map((g) => <span key={g}>{g}</span>)}
            </div>
            <p className="mt-2 font-display text-base italic leading-snug text-ink-800" data-testid="lucky-reason">
              “{pick.reason}”
            </p>
            {detail?.overview && (
              <p className="mt-2 line-clamp-4 text-sm leading-relaxed text-ink-500" data-testid="lucky-overview">
                {detail.overview}
              </p>
            )}
            {watch && watch.flatrate.length > 0 && (
              <div className="mt-3" data-testid="lucky-watch">
                <div className="font-mono text-[10px] uppercase tracking-stamp text-ink-500">
                  Streaming in {watch.region}
                </div>
                <div className="mt-1.5 flex flex-wrap items-center gap-2">
                  {watch.flatrate.slice(0, 5).map((p) =>
                    p.logoUrl ? (
                      <img
                        key={p.name}
                        src={p.logoUrl}
                        alt={p.name}
                        title={p.name}
                        loading="lazy"
                        className="h-7 w-7 rounded-md border border-border-soft"
                      />
                    ) : (
                      <span key={p.name} className="rounded-md border border-border-soft px-2 py-1 text-[11px] text-ink-600">
                        {p.name}
                      </span>
                    ),
                  )}
                  {watch.link && (
                    <a
                      href={watch.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="mono-meta text-ink-400 underline hover:text-red-600"
                    >
                      where to watch →
                    </a>
                  )}
                </div>
              </div>
            )}
            {detail?.tmdbId && (
              <a
                href={`https://www.themoviedb.org/movie/${detail.tmdbId}`}
                target="_blank"
                rel="noopener noreferrer"
                className="mono-meta mt-2 inline-block text-ink-400 underline hover:text-red-600"
              >
                More on TMDB →
              </a>
            )}
          </div>
        </div>
      )}
    </section>
  );
}
